import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import axios from 'axios'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog'
import { useLanguage } from '../contexts/LanguageContext'
import LanguageToggle from '../components/LanguageToggle'
import ThemeMenu from '../components/ThemeMenu'
import ProfileButton from '../components/ProfileButton'
import AppFooter from '../components/AppFooter'
import BrandLogo from '../components/BrandLogo'
import DashboardButton from '../components/DashboardButton'
import { formatDate } from '../lib/translations'
import {
  getApiUrl,
  currencySymbol,
  formatMoney,
  GIFT_CURRENCIES, resolveImageUrl } from '../lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { WISHLIST_ICONS, wishlistEmoji } from '../lib/wishlistIcons'
import WishlistImageUpload from '../components/WishlistImageUpload'
import { exportInvitePdf } from '../lib/invitePdf'
import {
  Clock,
  Copy,
  Users,
  Gift,
  Plus,
  Loader2,
  CheckCircle,
  XCircle,
  HelpCircle,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  Activity,
  Send,
  Search,
  RefreshCw,
  ExternalLink,
  Smartphone,
  Monitor,
  Camera,
  FileDown,
  ChevronDown,
  Wallet,
} from 'lucide-react'
import { toast } from 'sonner'

const API = getApiUrl()

// Shared shell for the four host actions (Edit / Copy / PDF / WhatsApp). They
// used to be four solid blocks of unrelated colour; now they read as one set —
// the brand-pink outline and surface fill are identical across all four, and
// only the icon + label carry a colour, chosen to match what the action does
// (amber for editing, brand pink for copying, red for PDF, green for WhatsApp).
// The per-action tint is appended by each button as a `text-…` class.
const HOST_ACTION_BTN =
  'flex flex-col items-center justify-center gap-1 rounded-xl border border-[#ff4d8d]/35 bg-white/70 px-1 py-2.5 text-center text-[11px] font-semibold leading-none transition-colors hover:border-[#ff4d8d] hover:bg-[#ff4d8d]/[0.07] disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:border-[#ff4d8d]/35 disabled:hover:bg-white/70 dark:bg-white/[0.04] dark:hover:bg-[#ff4d8d]/[0.12] dark:disabled:hover:bg-white/[0.04]'

const EventDetail = () => {
  const { eventId } = useParams()
  const { language, t } = useLanguage()
  const [event, setEvent] = useState(null)
  const [guests, setGuests] = useState([])
  const [guestFilter, setGuestFilter] = useState('all')
  const [guestSort, setGuestSort] = useState('name') // name | status
  const [groupHouseholds, setGroupHouseholds] = useState(false)
  const [collapsedHouseholds, setCollapsedHouseholds] = useState<Set<string>>(
    new Set(),
  )
  const [activity, setActivity] = useState([])
  // Activity feed filters (client-side)
  const [activityType, setActivityType] = useState('all') // all | rsvp | gift | message
  const [activitySearch, setActivitySearch] = useState('')
  const [activityFrom, setActivityFrom] = useState('')
  const [activityTo, setActivityTo] = useState('')
  const [wishlist, setWishlist] = useState([])
  const [wallet, setWallet] = useState<any>(null)
  // The balance chip starts masked: the event page is the screen a host is most
  // likely to have open in front of guests.
  const [balanceShown, setBalanceShown] = useState(false)
  const [rsvpSummary, setRsvpSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  // Bumped after edits to force the invitation preview iframe to reload.
  const [previewKey, setPreviewKey] = useState(0)
  // Invitation preview can be shown at phone or desktop width. On small
  // screens the preview stacks under the management cards, so it starts in
  // phone mode there; the xl sidebar keeps its desktop default.
  const [previewDevice, setPreviewDevice] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia('(min-width: 1280px)').matches
      ? 'desktop'
      : 'phone'
  )
  // Measured size of the desktop preview viewport; used to scale a 1280px-wide
  // render of the invitation down to fit the narrow preview pane.
  // Seeded with a sane default so the desktop preview renders on first paint
  // instead of popping in once the ResizeObserver resolves.
  const [previewSize, setPreviewSize] = useState({ w: 336, h: 0 })
  const previewResizeObserver = useRef(null)
  const measurePreview = useCallback((node) => {
    if (previewResizeObserver.current) {
      previewResizeObserver.current.disconnect()
      previewResizeObserver.current = null
    }
    if (node) {
      const ro = new ResizeObserver((entries) => {
        const rect = entries[0].contentRect
        setPreviewSize({ w: rect.width, h: rect.height })
      })
      ro.observe(node)
      previewResizeObserver.current = ro
    }
  }, [])
  // Emulate a real desktop viewport: the preview shows the invite's first
  // screen only, and the page scrolls INSIDE the frame (RSVP click scrolls
  // down to the card there), exactly like a guest's monitor. The on-screen
  // frame fills the viewport below the sticky header; the emulated viewport
  // height is derived from it so the invite's 100vh first screen exactly
  // fills the frame.
  const DESKTOP_PREVIEW_WIDTH = 1280
  const desktopScale =
    previewSize.w > 0 ? previewSize.w / DESKTOP_PREVIEW_WIDTH : 0
  const desktopViewportH =
    previewSize.h > 0 && desktopScale > 0
      ? Math.round(previewSize.h / desktopScale)
      : 800

  // Guest form
  const [guestDialogOpen, setGuestDialogOpen] = useState(false)
  const [guestForm, setGuestForm] = useState({
    name: '',
    email: '',
    phone: '',
    plus_ones: 0,
  })
  const [editingGuest, setEditingGuest] = useState(null)
  const [savingGuest, setSavingGuest] = useState(false)

  // Wishlist form
  const [wishlistDialogOpen, setWishlistDialogOpen] = useState(false)
  const [wishlistForm, setWishlistForm] = useState({
    title: '',
    target_amount: '',
    currency: 'GEL',
    icon: 'gift',
    image_url: '',
  })
  const [editingItemId, setEditingItemId] = useState(null)
  const [savingWishlist, setSavingWishlist] = useState(false)
  // The wishlist card shows only the first 2 items until expanded; the guest
  // list and activity cards show the first 5.
  const [wishlistExpanded, setWishlistExpanded] = useState(false)
  const [guestsExpanded, setGuestsExpanded] = useState(false)
  const [activityExpanded, setActivityExpanded] = useState(false)

  // In-place cover photo editing (triggered from the invitation preview)
  const coverInputRef = useRef(null)

  // Quick edit — opened by clicking an editable element inside the invitation
  // preview iframe (the invite page posts { type: 'storyfy-edit', field }).
  const [quickEdit, setQuickEdit] = useState(null) // { field, value } | null
  const [savingQuickEdit, setSavingQuickEdit] = useState(false)

  // Wishlist progress bars animate their width from 0 the first time the list
  // scrolls into view (presentation only; reduced motion snaps instantly via
  // the motion-reduce CSS variant on the bar's transition).
  const [wishlistBarsInView, setWishlistBarsInView] = useState(false)
  const wishlistBarsObserver = useRef(null)
  const wishlistBarsRef = useCallback((node) => {
    if (wishlistBarsObserver.current) {
      wishlistBarsObserver.current.disconnect()
      wishlistBarsObserver.current = null
    }
    if (!node) return
    if (typeof IntersectionObserver === 'undefined') {
      setWishlistBarsInView(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setWishlistBarsInView(true)
          io.disconnect()
        }
      },
      { threshold: 0.15 },
    )
    io.observe(node)
    wishlistBarsObserver.current = io
  }, [])

  const fetchEventData = useCallback(async () => {
    try {
      const [eventRes, guestsRes, wishlistRes, rsvpRes, activityRes, walletRes] =
        await Promise.all([
          axios.get(`${API}/events/${eventId}`, { withCredentials: true }),
          axios.get(`${API}/events/${eventId}/guests`, { withCredentials: true }),
          axios.get(`${API}/events/${eventId}/wishlist`, {
            withCredentials: true,
          }),
          axios.get(`${API}/events/${eventId}/rsvp-summary`, {
            withCredentials: true,
          }),
          axios.get(`${API}/events/${eventId}/activity`, {
            withCredentials: true,
          }),
          // Same source as the wallet page, so the chip can't disagree with it.
          // Non-fatal: a wallet hiccup shouldn't blank the whole event page.
          axios
            .get(`${API}/events/${eventId}/wallet`, { withCredentials: true })
            .catch(() => null),
        ])
      setEvent(eventRes.data)
      setGuests(guestsRes.data)
      setWishlist(wishlistRes.data)
      setRsvpSummary(rsvpRes.data)
      setActivity(activityRes.data?.entries || [])
      setWallet(walletRes?.data || null)
    } catch (error) {
      toast.error(t.failedToLoad || 'Failed to load event')
    } finally {
      setLoading(false)
    }
  }, [eventId, t.failedToLoad])

  useEffect(() => {
    fetchEventData()
  }, [fetchEventData])

  // Listen for edit requests coming from the invitation preview iframe.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin) return
      const data = e.data
      if (!data || data.type !== 'storyfy-edit') return
      if (data.field === 'cover') {
        // A postMessage handler carries no user activation, so a direct
        // coverInputRef.click() would be blocked — route through the dialog,
        // whose button click is a real gesture.
        setQuickEdit({ field: 'cover', value: '' })
        return
      }
      const current = {
        title: event?.title,
        date: event?.date,
        time: event?.time,
        location: event?.location,
        description: event?.description,
        honoree_name: event?.honoree_name,
        dress_code: event?.dress_code,
        arrival_instructions: event?.arrival_instructions,
      }
      if (data.field in current) {
        setQuickEdit({ field: data.field, value: current[data.field] || '' })
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [event])

  const QUICK_EDIT_FIELDS = {
    cover: { ka: 'ფონის სურათი', en: 'Cover photo', type: 'cover' },
    title: { ka: 'სათაური', en: 'Title', type: 'text' },
    date: { ka: 'თარიღი', en: 'Date', type: 'date' },
    time: { ka: 'დრო', en: 'Time', type: 'time' },
    location: { ka: 'ადგილმდებარეობა', en: 'Location', type: 'text' },
    description: { ka: 'აღწერა', en: 'Description', type: 'textarea' },
    honoree_name: { ka: 'საპატიო სტუმარი', en: 'Guest of honor', type: 'text' },
    dress_code: { ka: 'დრეს-კოდი', en: 'Dress code', type: 'text' },
    arrival_instructions: { ka: 'დეტალები', en: 'Details', type: 'textarea' },
  }

  const saveQuickEdit = async () => {
    if (!quickEdit) return
    setSavingQuickEdit(true)
    try {
      await axios.put(
        `${API}/events/${eventId}`,
        { [quickEdit.field]: quickEdit.value },
        { withCredentials: true },
      )
      toast.success(language === 'ka' ? 'შენახულია' : 'Saved')
      setQuickEdit(null)
      fetchEventData()
      setPreviewKey((k) => k + 1)
    } catch (error) {
      const detail = error.response?.data?.detail
      toast.error(
        typeof detail === 'string'
          ? detail
          : language === 'ka'
            ? 'ვერ შეინახა'
            : 'Failed to save',
      )
    } finally {
      setSavingQuickEdit(false)
    }
  }

  // Get full invite URL
  const getInviteUrl = () => {
    return `${window.location.origin}/invite/${event?.invite_link}`
  }

  // Get formatted invite message
  const getInviteMessage = () => {
    const eventLang = event?.language || 'en'

    if (eventLang === 'both') {
      const kaDate = formatDate(event?.date, 'ka')
      const enDate = formatDate(event?.date, 'en')
      return `გიწვევ "${event?.title}"-ზე!\nYou're invited to "${event?.title}"!\n\n📅 ${kaDate} / ${enDate}\n⏰ ${event?.time}\n📍 ${event?.location}\n\nგთხოვ დაადასტურე აქ / Please RSVP here:\n${getInviteUrl()}`
    }

    const dateStr = formatDate(event?.date, eventLang)

    if (eventLang === 'ka') {
      return `გიწვევ "${event?.title}"-ზე!\n\n📅 ${dateStr}\n⏰ ${event?.time}\n📍 ${event?.location}\n\nგთხოვ დაადასტურე აქ:\n${getInviteUrl()}`
    }
    return `You're invited to "${event?.title}"!\n\n📅 ${dateStr}\n⏰ ${event?.time}\n📍 ${event?.location}\n\nPlease RSVP here:\n${getInviteUrl()}`
  }

  const copyInviteMessage = () => {
    navigator.clipboard.writeText(getInviteMessage())
    toast.success(
      language === 'ka' ? 'შეტყობინება დაკოპირდა!' : 'Invite message copied!',
    )
  }

  // Copy a guest's personal link for invite-only events: the invite URL with
  // their code pre-filled, so they never have to type it.
  const copyInviteCode = (code: string) => {
    navigator.clipboard.writeText(`${getInviteUrl()}?code=${code}`)
    toast.success(t.inviteCodeCopied)
  }

  const shareViaWhatsApp = () => {
    const message = encodeURIComponent(getInviteMessage())
    window.open(`https://wa.me/?text=${message}`, '_blank')
  }

  // Open a branded, QR-coded invitation in a print window (Save as PDF).
  const handleExportPdf = async () => {
    try {
      await exportInvitePdf(event, getInviteUrl(), language)
    } catch (e) {
      toast.error(
        language === 'ka'
          ? 'PDF ვერ გაიხსნა — დაუშვი pop-up ფანჯრები'
          : 'Could not open the PDF — please allow pop-ups',
      )
    }
  }

  // Guest handlers
  const handleSaveGuest = async () => {
    if (!guestForm.name) {
      toast.error(
        language === 'ka' ? 'სახელი სავალდებულოა' : 'Name is required',
      )
      return
    }
    // Backend accepts 4-15 digits (optional +); catch it here so the host sees
    // a clear message instead of a generic 422 failure.
    const phoneDigits = guestForm.phone.replace(/\D/g, '')
    if (guestForm.phone && (phoneDigits.length < 4 || phoneDigits.length > 15)) {
      toast.error(
        language === 'ka'
          ? 'ტელეფონის ნომერი უნდა შეიცავდეს 4-15 ციფრს'
          : 'Phone number must contain 4-15 digits',
      )
      return
    }
    setSavingGuest(true)
    const payload = {
      ...guestForm,
      // Legacy boolean kept in sync for older backend rows/clients.
      plus_one: guestForm.plus_ones > 0,
    }
    try {
      if (editingGuest) {
        await axios.put(
          `${API}/events/${eventId}/guests/${editingGuest.guest_id}`,
          payload,
          { withCredentials: true },
        )
        toast.success(language === 'ka' ? 'სტუმარი განახლდა' : 'Guest updated')
      } else {
        await axios.post(`${API}/events/${eventId}/guests`, payload, {
          withCredentials: true,
        })
        toast.success(language === 'ka' ? 'სტუმარი დაემატა' : 'Guest added')
      }
      setGuestDialogOpen(false)
      setGuestForm({ name: '', email: '', phone: '', plus_ones: 0 })
      setEditingGuest(null)
      fetchEventData()
    } catch (error) {
      const detail = error.response?.data?.detail
      toast.error(
        typeof detail === 'string'
          ? detail
          : language === 'ka'
            ? 'ვერ შეინახა'
            : 'Failed to save guest',
      )
    } finally {
      setSavingGuest(false)
    }
  }

  const handleEditGuest = (guest) => {
    setEditingGuest(guest)
    setGuestForm({
      name: guest.name,
      email: guest.email || '',
      // Sanitize legacy free-text phones on load so re-saving an untouched
      // guest doesn't trip the backend's digits-only validation.
      phone: (guest.phone || '').replace(/[^0-9+ ]/g, ''),
      plus_ones: guest.plus_ones > 0 ? guest.plus_ones : guest.plus_one ? 1 : 0,
    })
    setGuestDialogOpen(true)
  }

  const handleDeleteGuest = async (guestId) => {
    if (
      !window.confirm(
        language === 'ka' ? 'წაშალო ეს სტუმარი?' : 'Delete this guest?',
      )
    )
      return
    try {
      await axios.delete(`${API}/events/${eventId}/guests/${guestId}`, {
        withCredentials: true,
      })
      toast.success(language === 'ka' ? 'სტუმარი წაიშალა' : 'Guest deleted')
      fetchEventData()
    } catch (error) {
      toast.error(language === 'ka' ? 'ვერ წაიშალა' : 'Failed to delete guest')
    }
  }

  const handleMarkInviteSent = async (guestId) => {
    try {
      await axios.post(
        `${API}/events/${eventId}/guests/${guestId}/mark-sent`,
        {},
        { withCredentials: true },
      )
      toast.success(
        language === 'ka' ? 'მონიშნულია გაგზავნილად' : 'Marked as sent',
      )
      fetchEventData()
    } catch (error) {
      toast.error(language === 'ka' ? 'ვერ განახლდა' : 'Failed to update')
    }
  }

  // Wishlist handlers
  const handleSaveWishlistItem = async () => {
    const targetAmount = parseFloat(wishlistForm.target_amount)
    if (!wishlistForm.title || !(targetAmount > 0)) {
      toast.error(
        language === 'ka'
          ? 'სახელი და თანხა სავალდებულოა'
          : 'Title and target amount are required',
      )
      return
    }
    if (targetAmount > 1000000) {
      toast.error(
        language === 'ka'
          ? 'მაქსიმალური თანხაა 1,000,000'
          : 'Maximum amount is 1,000,000',
      )
      return
    }
    setSavingWishlist(true)
    const payload = {
      ...wishlistForm,
      target_amount: targetAmount,
    }
    try {
      if (editingItemId) {
        await axios.put(
          `${API}/events/${eventId}/wishlist/${editingItemId}`,
          payload,
          { withCredentials: true },
        )
        toast.success(language === 'ka' ? 'ნივთი განახლდა' : 'Item updated')
      } else {
        await axios.post(`${API}/events/${eventId}/wishlist`, payload, {
          withCredentials: true,
        })
        toast.success(
          language === 'ka' ? 'ნივთი დაემატა' : 'Wishlist item added',
        )
      }
      closeWishlistDialog()
      fetchEventData()
      setPreviewKey((k) => k + 1)
    } catch (error) {
      const detail = error.response?.data?.detail
      toast.error(
        typeof detail === 'string'
          ? detail
          : language === 'ka'
            ? 'ვერ შეინახა'
            : 'Failed to save item',
      )
    } finally {
      setSavingWishlist(false)
    }
  }

  const closeWishlistDialog = () => {
    setWishlistDialogOpen(false)
    setEditingItemId(null)
    setWishlistForm({
      title: '',
      target_amount: '',
      currency: 'GEL',
      icon: 'gift',
      image_url: '',
    })
  }

  const openEditWishlistItem = (item) => {
    setEditingItemId(item.item_id)
    setWishlistForm({
      title: item.title,
      target_amount: String(item.target_amount),
      currency: item.currency || 'GEL',
      icon: item.icon || 'gift',
      image_url: item.image_url || '',
    })
    setWishlistDialogOpen(true)
  }

  const handleDeleteWishlistItem = async (itemId) => {
    if (
      !window.confirm(
        language === 'ka' ? 'წაშალო ეს ნივთი?' : 'Delete this item?',
      )
    )
      return
    try {
      await axios.delete(`${API}/events/${eventId}/wishlist/${itemId}`, {
        withCredentials: true,
      })
      toast.success(language === 'ka' ? 'ნივთი წაიშალა' : 'Item deleted')
      fetchEventData()
      setPreviewKey((k) => k + 1)
    } catch (error) {
      toast.error(language === 'ka' ? 'ვერ წაიშალა' : 'Failed to delete item')
    }
  }

  // Items with contributions are a financial record — they can only be hidden
  // from the guest invitation, never deleted (the backend enforces this too).
  const handleToggleHideItem = async (item) => {
    try {
      await axios.put(
        `${API}/events/${eventId}/wishlist/${item.item_id}`,
        { hidden: !item.hidden },
        { withCredentials: true },
      )
      toast.success(
        item.hidden
          ? language === 'ka'
            ? 'ნივთი კვლავ ჩანს სტუმრებთან'
            : 'Item is visible to guests again'
          : language === 'ka'
            ? 'ნივთი დაიმალა სტუმრებისგან'
            : 'Item hidden from guests',
      )
      fetchEventData()
      setPreviewKey((k) => k + 1)
    } catch (error) {
      toast.error(language === 'ka' ? 'ვერ განახლდა' : 'Failed to update')
    }
  }

  // Upload a new cover photo and persist it on the event, in place.
  const handleCoverUpload = async (file) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error(language === 'ka' ? 'აირჩიე სურათი' : 'Please select an image file')
      return
    }
    // Uploading feedback comes via the success/error toasts below.
    toast.message(language === 'ka' ? 'იტვირთება…' : 'Uploading…')
    const fd = new FormData()
    fd.append('file', file)
    try {
      const { data } = await axios.post(`${API}/upload/image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      await axios.put(
        `${API}/events/${eventId}`,
        { cover_image: data.url },
        { withCredentials: true },
      )
      toast.success(language === 'ka' ? 'ფონის სურათი განახლდა' : 'Cover photo updated')
      fetchEventData()
      setPreviewKey((k) => k + 1)
    } catch (error) {
      toast.error(language === 'ka' ? 'ვერ აიტვირთა' : 'Failed to update cover photo')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-celebrate flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#ff4d8d]" />
      </div>
    )
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-celebrate flex items-center justify-center">
        <p className="text-[#4a3d66] dark:text-gray-400">
          {language === 'ka' ? 'ღონისძიება ვერ მოიძებნა' : 'Event not found'}
        </p>
      </div>
    )
  }

  // Wishlist items can be priced in different currencies; totals are per currency.
  const wishlistTotals = wishlist.reduce((acc, item) => {
    const cur = item.currency || 'GEL'
    const totals = acc[cur] || { collected: 0, target: 0 }
    totals.collected += item.collected_amount
    totals.target += item.target_amount
    acc[cur] = totals
    return acc
  }, {} as Record<string, { collected: number; target: number }>)
  const visibleGuests = guests.filter(
    (g) => guestFilter === 'all' || g.rsvp_status === guestFilter,
  )
  // Sort the (filtered) guest list. "status" groups by RSVP state, then name;
  // default is alphabetical.
  const guestStatusOrder: Record<string, number> = {
    attending: 0,
    maybe: 1,
    pending: 2,
    declined: 3,
  }
  const sortedGuests = [...visibleGuests].sort((a, b) => {
    if (guestSort === 'status') {
      const d =
        (guestStatusOrder[a.rsvp_status] ?? 9) -
        (guestStatusOrder[b.rsvp_status] ?? 9)
      if (d !== 0) return d
    }
    return (a.name || '').localeCompare(b.name || '')
  })

  // Household grouping (non-destructive): consolidate guests who share a last
  // name into collapsible groups; singletons stay as normal rows.
  const householdKey = (g: any) => {
    const parts = (g.name || '').trim().split(/\s+/)
    return (parts.length > 1 ? parts[parts.length - 1] : g.name || '—').toLowerCase()
  }
  const toggleHousehold = (key: string) =>
    setCollapsedHouseholds((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  const guestDisplayItems: any[] = []
  if (!groupHouseholds) {
    sortedGuests.forEach((g) => guestDisplayItems.push({ type: 'guest', guest: g }))
  } else {
    const groups: Record<string, any[]> = {}
    const order: string[] = []
    sortedGuests.forEach((g) => {
      const key = householdKey(g)
      if (!groups[key]) {
        groups[key] = []
        order.push(key)
      }
      groups[key].push(g)
    })
    order.forEach((key) => {
      const members = groups[key]
      if (members.length >= 2) {
        const surname =
          (members[0].name || '').trim().split(/\s+/).slice(-1)[0] || members[0].name
        guestDisplayItems.push({ type: 'header', key, surname, count: members.length })
        if (!collapsedHouseholds.has(key)) {
          members.forEach((g) => guestDisplayItems.push({ type: 'guest', guest: g }))
        }
      } else {
        guestDisplayItems.push({ type: 'guest', guest: members[0] })
      }
    })
  }

  // Gift contributions grouped per wishlist item (from the activity feed)
  const giftsByItem = activity.reduce((acc, e) => {
    if (e.type === 'gift' && e.item_id) {
      acc[e.item_id] = acc[e.item_id] || []
      acc[e.item_id].push(e)
    }
    return acc
  }, {})

  // Most recently gifted items float to the top of the wishlist; items with no
  // contributions keep their original order below (sort is stable).
  const lastGiftAt: Record<string, string> = {}
  activity.forEach((e) => {
    if (e.type === 'gift' && e.item_id && e.created_at) {
      if (!lastGiftAt[e.item_id] || e.created_at > lastGiftAt[e.item_id]) {
        lastGiftAt[e.item_id] = e.created_at
      }
    }
  })
  const sortedWishlist = [...wishlist].sort((a, b) =>
    (lastGiftAt[b.item_id] || '').localeCompare(lastGiftAt[a.item_id] || ''),
  )

  // Apply the activity feed filters (type / guest-name search / date range).
  const activitySearchLc = activitySearch.trim().toLowerCase()
  const filteredActivity = activity.filter((e) => {
    if (activityType === 'rsvp' && e.type !== 'rsvp') return false
    if (activityType === 'gift' && e.type !== 'gift') return false
    if (activityType === 'message' && !(e.type === 'rsvp' && e.message)) return false
    if (activitySearchLc && !(e.name || '').toLowerCase().includes(activitySearchLc))
      return false
    if (e.created_at) {
      const day = e.created_at.slice(0, 10) // YYYY-MM-DD
      if (activityFrom && day < activityFrom) return false
      if (activityTo && day > activityTo) return false
    }
    return true
  })
  const activityFiltersActive =
    activityType !== 'all' || !!activitySearchLc || !!activityFrom || !!activityTo

  const wishlistTotalsLabel = Object.keys(wishlistTotals)
    .map(
      (cur) =>
        `${currencySymbol(cur)}${formatMoney(wishlistTotals[cur].collected)} / ${currencySymbol(cur)}${formatMoney(wishlistTotals[cur].target)}`,
    )
    .join(' · ')

  // The event's net payout — what the wallet page calls "your net payout", not
  // the gross, so the chip and that page can't quote different numbers. null
  // until the wallet loads or when nothing has been raised, which hides the
  // chip rather than showing a bare zero.
  const walletNet =
    wallet?.currencies?.length
      ? wallet.currencies
          .filter((c) => c.gross > 0)
          .map((c) => `${currencySymbol(c.currency)}${formatMoney(c.net)}`)
          .join(' · ') || null
      : null

  return (
    <div className="min-h-screen bg-celebrate flex flex-col">
      {/* Header */}
      <header className="bg-white/80 dark:bg-[#1e1438]/80 backdrop-blur-xl border-b border-[#1b1033]/10 dark:border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <BrandLogo className="mr-3" />
            <DashboardButton />
            <div className="flex-1 min-w-0" />
            <div className="flex items-center gap-2 sm:gap-4 ml-4">
              <LanguageToggle />
              <ThemeMenu />
              <ProfileButton />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-[calc(100dvh-4rem)] mx-auto w-full px-4 sm:px-6 xl:px-14 py-8">
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 xl:gap-20">
          {/* Management column — the host's original event controls */}
          <div className="min-w-0 flex flex-col">
          {/* Hidden cover-photo file input — the invitation preview's cover
              quick-edit routes uploads through it. */}
          <input
            ref={coverInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleCoverUpload(e.target.files?.[0])}
            data-testid="cover-file-input"
          />

          {/* Host actions — edit, share, copy, PDF, WhatsApp. Kept in the always-
              visible management column so the organizer can reach them at any
              width; the full invitation preview renders in the second grid
              cell (sticky sidebar on xl, stacked below on smaller screens).

              The container renders unconditionally: it used to be hidden whole
              whenever invite_link was missing, so an event awaiting its link
              silently lost Edit too and the host was left with no controls at
              all. Now only the link-dependent actions go disabled, with a line
              saying why. */}
          <div className="glass-card rounded-xl p-3 mb-4" data-testid="host-actions-bar">
            <div className="grid grid-cols-4 gap-2">
              <Link
                to={`/events/${eventId}/edit`}
                className={`${HOST_ACTION_BTN} text-[#b45309] dark:text-[#fbbf24]`}
                data-testid="edit-event-btn"
              >
                <Edit className="h-4 w-4" />
                {language === 'ka' ? 'რედაქტირება' : 'Edit'}
              </Link>
              <button
                type="button"
                onClick={copyInviteMessage}
                disabled={!event.invite_link}
                className={`${HOST_ACTION_BTN} text-[#ff4d8d]`}
                data-testid="host-copy-message-btn"
              >
                <Copy className="h-4 w-4" />
                {language === 'ka' ? 'კოპირება' : 'Copy'}
              </button>
              <button
                type="button"
                onClick={handleExportPdf}
                disabled={!event.invite_link}
                className={`${HOST_ACTION_BTN} text-[#dc2626] dark:text-[#f87171]`}
                data-testid="export-pdf-btn"
              >
                <FileDown className="h-4 w-4" />
                PDF
              </button>
              <button
                type="button"
                onClick={shareViaWhatsApp}
                disabled={!event.invite_link}
                className={`${HOST_ACTION_BTN} text-[#128c4a] dark:text-[#4ade80]`}
                data-testid="host-whatsapp-btn"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                WhatsApp
              </button>
            </div>
            {!event.invite_link && (
              <p className="mt-2 text-center text-[11px] text-[#4a3d66] dark:text-gray-400">
                {language === 'ka'
                  ? 'მოწვევის ბმული ჯერ არ არის მზად — გაზიარება მალე გააქტიურდება.'
                  : 'The invitation link is not ready yet — sharing unlocks once it is.'}
              </p>
            )}
          </div>

          {/* Wishlist card */}
          <div className="mb-4 min-w-0">
            <div className="overflow-hidden rounded-2xl border border-[#1b1033]/[0.07] bg-[var(--surface)] shadow-[0_1px_3px_rgba(27,16,51,0.06)] dark:border-white/10 dark:shadow-none">
              <div className="flex items-center gap-3 border-b border-[#1b1033]/[0.06] px-4 py-2.5 dark:border-white/10">
                <h3 className="text-base font-semibold tracking-tight text-[#1b1033] dark:text-[#f4f0ff] font-display">
                  {t.wishlist}
                </h3>
                {wishlist.length > 0 && (
                  <span className="min-w-0 truncate text-[13px] text-[#4a3d66] dark:text-gray-400 tabular-nums">
                    {wishlistTotalsLabel}
                  </span>
                )}
                {/* The host keeps their list either way; this says whether
                    guests can currently see it (types like a corporate event
                    default the section off). */}
                {event.show_gifts === false && (
                  <span
                    className="shrink-0 rounded-md bg-[#1b1033]/[0.06] px-1.5 py-0.5 text-[11px] font-medium text-[#4a3d66] dark:bg-white/10 dark:text-gray-300"
                    title={
                      language === 'ka'
                        ? 'სტუმრები ამ სიას ვერ ხედავენ — ჩართე რედაქტირება → სტუმრები.'
                        : 'Guests cannot see this list — turn it on in Edit → Guests.'
                    }
                    data-testid="gifts-hidden-badge"
                  >
                    {language === 'ka' ? 'დამალულია' : 'Hidden'}
                  </span>
                )}
                {/* Wallet balance, left of the button. Money on a screen the
                    host may open in front of guests, so it starts masked and
                    only they can reveal it. On narrow screens the amount stays
                    (it's the point) while the button's label drops. */}
                <div className="ml-auto flex shrink-0 items-center gap-1">
                  {walletNet !== null && (
                    <button
                      type="button"
                      onClick={() => setBalanceShown((v) => !v)}
                      title={balanceShown ? t.hideBalance : t.showBalance}
                      aria-label={balanceShown ? t.hideBalance : t.showBalance}
                      aria-pressed={balanceShown}
                      className="inline-flex h-8 items-center gap-1.5 rounded-lg px-1.5 text-[13px] font-semibold tabular-nums text-[#1b1033] transition-colors hover:bg-[#ff4d8d]/[0.06] dark:text-[#f4f0ff]"
                      data-testid="event-wallet-balance"
                    >
                      {balanceShown ? (
                        <Eye className="h-3.5 w-3.5 shrink-0 text-[#4a3d66] dark:text-gray-400" />
                      ) : (
                        <EyeOff className="h-3.5 w-3.5 shrink-0 text-[#4a3d66] dark:text-gray-400" />
                      )}
                      <span className={balanceShown ? '' : 'tracking-[0.15em]'}>
                        {balanceShown ? walletNet : '••••'}
                      </span>
                    </button>
                  )}
                  <Button
                    asChild
                    variant="outline"
                    className="h-8 shrink-0 rounded-lg border-[#ff4d8d]/30 px-2.5 text-sm font-medium text-[#1b1033] hover:bg-[#ff4d8d]/[0.06] hover:text-[#ff4d8d] dark:text-[#f4f0ff] sm:px-3"
                  >
                    <Link to={`/events/${eventId}/wallet`} data-testid="event-wallet-link">
                      <Wallet className="h-3.5 w-3.5 text-[#ff4d8d] sm:mr-1.5" />
                      <span className="hidden sm:inline">
                        {language === 'ka' ? 'საფულე' : 'Wallet'}
                      </span>
                    </Link>
                  </Button>
                </div>
                <Dialog
                  open={wishlistDialogOpen}
                  onOpenChange={(open) => {
                    if (open) {
                      setWishlistDialogOpen(true)
                    } else {
                      closeWishlistDialog()
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button
                      className="h-8 shrink-0 rounded-lg bg-[#ff4d8d] px-3 text-sm font-medium text-white shadow-none hover:bg-[#e8437f]"
                      data-testid="add-wishlist-btn"
                    >
                      <Plus className="mr-1.5 h-3.5 w-3.5" />
                      {t.addItem}
                    </Button>
                  </DialogTrigger>
                  <DialogContent
                    className="sm:max-w-md"
                    aria-describedby={undefined}
                  >
                    <DialogHeader>
                      <DialogTitle className="font-display">
                        {editingItemId
                          ? language === 'ka'
                            ? 'ნივთის რედაქტირება'
                            : 'Edit item'
                          : t.addItem}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label className="text-sm font-medium text-[#1b1033] dark:text-[#f4f0ff] mb-1.5 block">
                          {t.itemTitle} *
                        </Label>
                        <Input
                          value={wishlistForm.title}
                          onChange={(e) =>
                            setWishlistForm({
                              ...wishlistForm,
                              title: e.target.value,
                            })
                          }
                          placeholder={t.itemTitlePlaceholder}
                          className="h-11 rounded-xl"
                          data-testid="wishlist-title-input"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-[#1b1033] dark:text-[#f4f0ff] mb-1.5 block">
                          {language === 'ka' ? 'ხატულა' : 'Icon'}
                        </Label>
                        <div
                          className="grid grid-cols-8 gap-1.5"
                          data-testid="wishlist-icon-picker"
                        >
                          {WISHLIST_ICONS.map(({ id, emoji }) => (
                            <button
                              key={id}
                              type="button"
                              onClick={() =>
                                setWishlistForm({ ...wishlistForm, icon: id })
                              }
                              aria-label={id}
                              aria-pressed={wishlistForm.icon === id}
                              className={`flex h-9 items-center justify-center rounded-lg border text-lg leading-none transition-colors ${
                                wishlistForm.icon === id
                                  ? 'border-[#ff4d8d] bg-[#ff4d8d]/10'
                                  : 'border-[#1b1033]/10 hover:border-[#1b1033]/25 dark:border-white/10 dark:hover:border-white/25'
                              }`}
                              data-testid={`wishlist-icon-${id}`}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-[#1b1033] dark:text-[#f4f0ff] mb-1.5 block">
                          {language === 'ka' ? 'სურათი' : 'Picture'}
                        </Label>
                        <WishlistImageUpload
                          value={wishlistForm.image_url}
                          onChange={(url) => setWishlistForm({ ...wishlistForm, image_url: url })}
                          appLang={language}
                          testId="wishlist-image"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-[#1b1033] dark:text-[#f4f0ff] mb-1.5 block">
                          {t.targetAmount} *
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            // type="text" (not "number"): controlled number
                            // inputs drop everything after the first digit on
                            // some mobile keyboards; sanitize instead.
                            type="text"
                            inputMode="decimal"
                            value={wishlistForm.target_amount}
                            onChange={(e) => {
                              // Comma-decimal keyboards (ka-GE) type ',' — treat
                              // it as the decimal point instead of dropping it.
                              const v = e.target.value
                                .replace(/,/g, '.')
                                .replace(/[^0-9.]/g, '')
                                .replace(/(\..*)\./g, '$1')
                              setWishlistForm({
                                ...wishlistForm,
                                target_amount: v,
                              })
                            }}
                            placeholder="500"
                            maxLength={9}
                            className="h-11 rounded-xl flex-1"
                            data-testid="wishlist-amount-input"
                          />
                          <Select
                            value={wishlistForm.currency}
                            onValueChange={(value) =>
                              setWishlistForm({
                                ...wishlistForm,
                                currency: value,
                              })
                            }
                            disabled={
                              !!editingItemId &&
                              (wishlist.find(
                                (i) => i.item_id === editingItemId,
                              )?.collected_amount || 0) > 0
                            }
                          >
                            <SelectTrigger
                              className="w-28 h-11 rounded-xl"
                              data-testid="wishlist-currency-select"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {GIFT_CURRENCIES.map((cur) => (
                                <SelectItem key={cur} value={cur}>
                                  {currencySymbol(cur)} {cur}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Button
                        onClick={handleSaveWishlistItem}
                        disabled={savingWishlist}
                        className="w-full h-11 btn-gradient text-white rounded-xl"
                        data-testid="save-wishlist-btn"
                      >
                        {savingWishlist ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : editingItemId ? (
                          language === 'ka' ? 'შენახვა' : 'Save'
                        ) : (
                          t.addItem
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {wishlist.length === 0 ? (
                <div className="p-8 text-center">
                  <Gift className="mx-auto mb-2 h-8 w-8 text-[#1b1033]/15 dark:text-white/15" />
                  <p className="text-sm text-[#4a3d66] dark:text-gray-400">{t.noWishlistItems}</p>
                </div>
              ) : (
                <div
                  ref={wishlistBarsRef}
                  className="divide-y divide-[#1b1033]/[0.06] dark:divide-white/[0.08]"
                >
                  {(wishlistExpanded ? sortedWishlist : sortedWishlist.slice(0, 2)).map((item, wi) => {
                    const progress =
                      item.target_amount > 0
                        ? (item.collected_amount / item.target_amount) * 100
                        : 0
                    const isFunded = item.collected_amount >= item.target_amount
                    return (
                      <div
                        key={item.item_id}
                        className={`px-4 py-3 ${isFunded ? 'bg-green-50/50 dark:bg-green-500/10 opacity-75' : ''} ${item.hidden ? 'opacity-50' : ''}`}
                        data-testid={`wishlist-item-${item.item_id}`}
                      >
                        <div className="mb-1.5 flex items-center justify-between gap-2">
                          <div className="flex min-w-0 items-center gap-2">
                            {item.image_url ? (
                              <img
                                src={resolveImageUrl(item.image_url)}
                                alt=""
                                className="h-9 w-9 shrink-0 rounded-md border border-[#1b1033]/10 object-cover dark:border-white/10"
                                data-testid={`wishlist-item-photo-${item.item_id}`}
                              />
                            ) : (
                              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#1b1033]/[0.04] dark:bg-white/10 text-sm leading-none">
                                {wishlistEmoji(item.icon)}
                              </span>
                            )}
                            <h4 className="truncate text-[15px] font-medium text-[#1b1033] dark:text-[#f4f0ff]">
                              {item.title}
                            </h4>
                            {isFunded && (
                              <span className="shrink-0 rounded-md bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-500/15 dark:text-green-300">
                                {t.funded}
                              </span>
                            )}
                            {item.hidden && (
                              <span className="shrink-0 rounded-md bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-500 dark:bg-white/10 dark:text-gray-400">
                                {language === 'ka' ? 'დამალულია' : 'Hidden'}
                              </span>
                            )}
                          </div>
                          <div className="flex shrink-0 items-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditWishlistItem(item)}
                            title={language === 'ka' ? 'რედაქტირება' : 'Edit'}
                            aria-label={language === 'ka' ? 'რედაქტირება' : 'Edit'}
                            className="h-7 w-7 rounded-lg"
                            data-testid={`edit-item-${item.item_id}`}
                          >
                            <Edit className="h-3.5 w-3.5 text-[#4a3d66] dark:text-gray-400" />
                          </Button>
                          {(item.collected_amount || 0) > 0 ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleHideItem(item)}
                              title={
                                item.hidden
                                  ? language === 'ka'
                                    ? 'გამოჩენა'
                                    : 'Show to guests'
                                  : language === 'ka'
                                    ? 'დამალვა'
                                    : 'Hide from guests'
                              }
                              aria-label={
                                item.hidden
                                  ? language === 'ka'
                                    ? 'გამოჩენა'
                                    : 'Show to guests'
                                  : language === 'ka'
                                    ? 'დამალვა'
                                    : 'Hide from guests'
                              }
                              className="h-7 w-7 rounded-lg"
                              data-testid={`toggle-hide-${item.item_id}`}
                            >
                              {item.hidden ? (
                                <Eye className="h-3.5 w-3.5 text-[#4a3d66] dark:text-gray-400" />
                              ) : (
                                <EyeOff className="h-3.5 w-3.5 text-[#4a3d66] dark:text-gray-400" />
                              )}
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                handleDeleteWishlistItem(item.item_id)
                              }
                              aria-label={language === 'ka' ? 'წაშლა' : 'Delete item'}
                              className="h-7 w-7 rounded-lg"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-red-500" />
                            </Button>
                          )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div
                            className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-[#f1ecf6] dark:bg-white/10"
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={Math.round(Math.min(progress, 100))}
                          >
                            <div
                              className={`h-full rounded-full ${
                                wi % 2 === 1 ? 'bg-grad-cool' : 'bg-grad-hot'
                              } transition-[width] duration-1000 ease-out motion-reduce:transition-none`}
                              style={{
                                width: `${wishlistBarsInView ? Math.min(progress, 100) : 0}%`,
                              }}
                            />
                          </div>
                          <span className="min-w-[98px] text-right text-[13px] font-medium tabular-nums whitespace-nowrap text-[#1b1033] dark:text-[#f4f0ff]">
                            {currencySymbol(item.currency)}
                            {formatMoney(item.collected_amount)}
                            <span className="font-normal text-[#4a3d66] dark:text-gray-400">
                              {' '}/ {currencySymbol(item.currency)}
                              {formatMoney(item.target_amount)}
                            </span>
                          </span>
                        </div>
                        {(giftsByItem[item.item_id] || []).length > 0 && (
                          <div className="mt-1.5 space-y-0.5">
                            {giftsByItem[item.item_id].map((g, gi) => (
                              <p
                                key={gi}
                                className="text-xs text-[#4a3d66] dark:text-gray-400"
                              >
                                <span className="font-medium text-[#4a3d66] dark:text-gray-300">
                                  {g.name}
                                </span>
                                {' · '}
                                {currencySymbol(g.currency)}
                                {formatMoney(g.amount)}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
              {wishlist.length > 2 && (
                <button
                  type="button"
                  onClick={() => setWishlistExpanded((v) => !v)}
                  className="flex w-full items-center justify-center gap-1.5 border-t border-[#1b1033]/[0.06] py-2 text-[13px] font-medium text-[#ff4d8d] transition-colors hover:bg-[#1b1033]/[0.02] dark:border-white/10 dark:hover:bg-white/5"
                  data-testid="wishlist-show-more"
                >
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${wishlistExpanded ? 'rotate-180' : ''}`}
                  />
                  {wishlistExpanded
                    ? language === 'ka'
                      ? 'ნაკლების ჩვენება'
                      : 'Show less'
                    : language === 'ka'
                      ? `მეტის ნახვა (${wishlist.length - 2})`
                      : `Show more (${wishlist.length - 2})`}
                </button>
              )}
            </div>
          </div>

          {/* Guest list — the RSVP summary doubles as the status switcher at the top of the same card */}
          <div className="mb-4 min-w-0">
            <div className="flex flex-col overflow-hidden rounded-2xl border border-[#1b1033]/[0.07] bg-[var(--surface)] shadow-[0_1px_3px_rgba(27,16,51,0.06)] dark:border-white/10 dark:shadow-none">
              {/* RSVP summary switcher — click a tab to filter the guest list by that status */}
              {rsvpSummary && (
                <div className="grid grid-cols-2 border-b border-[#1b1033]/[0.06] dark:border-white/10 md:grid-cols-4">
                  {[
                    {
                      id: 'all',
                      // Total headcount (invited guests plus attending plus-ones,
                      // when the API provides the higher headcount).
                      value: Math.max(
                        rsvpSummary.total,
                        rsvpSummary.attending_headcount || 0,
                      ),
                      label: language === 'ka' ? 'სულ' : 'Total',
                      valueClass: 'text-[#1b1033] dark:text-[#f4f0ff]',
                      activeBar: 'bg-[#1b1033] dark:bg-white',
                    },
                    {
                      id: 'attending',
                      value: rsvpSummary.attending,
                      label: language === 'ka' ? 'მოდის' : t.attending,
                      valueClass: 'text-green-600 dark:text-green-400',
                      activeBar: 'bg-green-500',
                    },
                    {
                      id: 'declined',
                      value: rsvpSummary.declined,
                      label: language === 'ka' ? 'ვერ მოდის' : t.declined,
                      valueClass: 'text-red-600 dark:text-red-400',
                      activeBar: 'bg-red-500',
                    },
                    {
                      id: 'maybe',
                      value: rsvpSummary.maybe,
                      label: language === 'ka' ? 'შეიძლება' : t.maybe,
                      valueClass: 'text-amber-600 dark:text-amber-400',
                      activeBar: 'bg-amber-500',
                    },
                  ].map(({ id, value, label, valueClass, activeBar }) => {
                    const active = guestFilter === id
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setGuestFilter(id)}
                        aria-pressed={active}
                        data-testid={`rsvp-stat-${id}`}
                        className={`relative px-4 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#ff4d8d]/40 ${
                          active
                            ? 'bg-[#1b1033]/[0.02] dark:bg-white/[0.03]'
                            : 'hover:bg-[#1b1033]/[0.03] dark:hover:bg-white/[0.04]'
                        }`}
                      >
                        <p
                          className={`font-display text-xl font-semibold leading-none tabular-nums ${valueClass}`}
                        >
                          {value}
                        </p>
                        <p
                          className={`mt-0.5 text-xs font-medium ${
                            active
                              ? 'text-[#1b1033] dark:text-[#f4f0ff]'
                              : 'text-[#4a3d66] dark:text-gray-400'
                          }`}
                        >
                          {label}
                        </p>
                        {/* Active-tab underline — mounted only when active so no
                            transition can get stuck */}
                        {active && (
                          <span
                            aria-hidden
                            className={`pointer-events-none absolute inset-x-4 bottom-0 h-0.5 rounded-full ${activeBar}`}
                          />
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
              {/* Guest toolbar — sort + household grouping + add guest */}
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#1b1033]/[0.06] px-4 py-2 dark:border-white/10">
                {guests.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Select value={guestSort} onValueChange={setGuestSort}>
                      <SelectTrigger
                        className="h-8 w-[156px] rounded-lg border-[#1b1033]/[0.09] bg-transparent text-[13px] text-[#4a3d66] dark:border-white/10 dark:bg-transparent dark:text-gray-300"
                        aria-label={language === 'ka' ? 'დალაგება' : 'Sort'}
                        data-testid="guest-sort-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="name">
                          {language === 'ka' ? 'სახელით (ა–ჰ)' : 'Name (A–Z)'}
                        </SelectItem>
                        <SelectItem value="status">
                          {language === 'ka' ? 'RSVP სტატუსით' : 'RSVP status'}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={() => setGroupHouseholds((v) => !v)}
                      aria-pressed={groupHouseholds}
                      title={language === 'ka' ? 'ოჯახებად დაჯგუფება' : 'Group households'}
                      className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[13px] font-medium transition-colors ${
                        groupHouseholds
                          ? 'border-[#ff4d8d]/60 bg-[#ff4d8d]/[0.06] text-[#ff4d8d]'
                          : 'border-[#1b1033]/[0.09] text-[#4a3d66] hover:border-[#1b1033]/25 dark:border-white/10 dark:text-gray-300'
                      }`}
                      data-testid="group-households-toggle"
                    >
                      <Users className="h-3.5 w-3.5" />
                      {language === 'ka' ? 'ოჯახები' : 'Households'}
                    </button>
                  </div>
                ) : (
                  <span />
                )}
                <Dialog
                  open={guestDialogOpen}
                  onOpenChange={(open) => {
                    setGuestDialogOpen(open)
                    if (!open) {
                      setEditingGuest(null)
                      setGuestForm({
                        name: '',
                        email: '',
                        phone: '',
                        plus_ones: 0,
                      })
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button
                      className="h-8 shrink-0 rounded-lg bg-[#ff4d8d] px-3 text-sm font-medium text-white shadow-none hover:bg-[#e8437f]"
                      data-testid="add-guest-btn"
                    >
                      <Plus className="mr-1.5 h-3.5 w-3.5" />
                      {t.addGuest}
                    </Button>
                  </DialogTrigger>
                  <DialogContent
                    className="sm:max-w-md"
                    aria-describedby={undefined}
                  >
                    <DialogHeader>
                      <DialogTitle className="font-display">
                        {editingGuest ? t.editGuest : t.addGuest}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label className="text-sm font-medium text-[#1b1033] dark:text-[#f4f0ff] mb-1.5 block">
                          {t.guestName} *
                        </Label>
                        <Input
                          value={guestForm.name}
                          onChange={(e) =>
                            setGuestForm({ ...guestForm, name: e.target.value })
                          }
                          placeholder={t.guestNamePlaceholder}
                          maxLength={120}
                          className="h-11 rounded-xl"
                          data-testid="guest-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-[#1b1033] dark:text-[#f4f0ff] mb-1.5 block">
                          {t.email}
                        </Label>
                        <Input
                          type="email"
                          value={guestForm.email}
                          onChange={(e) =>
                            setGuestForm({
                              ...guestForm,
                              email: e.target.value,
                            })
                          }
                          placeholder="guest@email.com"
                          className="h-11 rounded-xl"
                          data-testid="guest-email-input"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-[#1b1033] dark:text-[#f4f0ff] mb-1.5 block">
                          {t.phone}
                        </Label>
                        <Input
                          type="tel"
                          inputMode="tel"
                          value={guestForm.phone}
                          onChange={(e) => {
                            // Digits and spaces only, plus a single leading +.
                            const raw = e.target.value.replace(/[^0-9+ ]/g, '')
                            const phone = raw.startsWith('+')
                              ? `+${raw.slice(1).replace(/\+/g, '')}`
                              : raw.replace(/\+/g, '')
                            setGuestForm({ ...guestForm, phone })
                          }}
                          placeholder="+995 5XX XX XX XX"
                          maxLength={20}
                          className="h-11 rounded-xl"
                          data-testid="guest-phone-input"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-[#1b1033] dark:text-[#f4f0ff] mb-1.5 block">
                          {t.plusOnesQuestion}
                        </Label>
                        <Select
                          value={String(guestForm.plus_ones)}
                          onValueChange={(value) =>
                            setGuestForm({
                              ...guestForm,
                              plus_ones: parseInt(value, 10) || 0,
                            })
                          }
                        >
                          <SelectTrigger
                            className="h-11 rounded-xl"
                            data-testid="guest-plusones-select"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">{t.plusOnesNone}</SelectItem>
                            {Array.from({ length: 10 }, (_, i) => i + 1).map(
                              (n) => (
                                <SelectItem key={n} value={String(n)}>
                                  +{n}
                                </SelectItem>
                              ),
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={handleSaveGuest}
                        disabled={savingGuest}
                        className="w-full h-11 btn-gradient text-white rounded-xl"
                        data-testid="save-guest-btn"
                      >
                        {savingGuest ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : editingGuest ? (
                          t.save
                        ) : (
                          t.addGuest
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {guests.length === 0 ? (
                <div className="p-8 text-center">
                  <Users className="mx-auto mb-2 h-8 w-8 text-[#1b1033]/15 dark:text-white/15" />
                  <p className="text-sm text-[#4a3d66] dark:text-gray-400">{t.noGuestsYet}</p>
                  {/* On an invite-only event an empty list is not just empty —
                      it means nobody can respond at all, because the personal
                      code a guest needs is minted when you add them here. */}
                  {event.rsvp_invite_only && (
                    <p
                      className="mx-auto mt-3 max-w-sm rounded-lg bg-[#8b5cf6]/10 px-3 py-2 text-[13px] text-[#4a3d66] dark:bg-[#8b5cf6]/15 dark:text-[#c3b8dd]"
                      data-testid="invite-only-empty-guests-note"
                    >
                      {language === 'ka'
                        ? 'ეს ღონისძიება დახურულია — პასუხის გაცემა მხოლოდ ამ სიაში მყოფებს შეუძლიათ. დაამატე სტუმრები და გაუგზავნე თითოეულს მისი პირადი კოდი.'
                        : 'This event is invite-only — only people on this list can respond. Add your guests, then send each of them the personal code that appears next to their name.'}
                    </p>
                  )}
                </div>
              ) : sortedGuests.length === 0 ? (
                <div className="p-6 text-center text-sm text-[#4a3d66] dark:text-gray-400">
                  {language === 'ka'
                    ? 'ამ სტატუსით სტუმარი არ არის'
                    : 'No guests with this status'}
                </div>
              ) : (
                <div className="divide-y divide-[#1b1033]/[0.06] dark:divide-white/[0.08]">
                  {(guestsExpanded
                    ? guestDisplayItems
                    : guestDisplayItems.slice(0, 5)
                  ).map((item) => {
                    if (item.type === 'header') {
                      const collapsed = collapsedHouseholds.has(item.key)
                      return (
                        <button
                          key={`hh-${item.key}`}
                          type="button"
                          onClick={() => toggleHousehold(item.key)}
                          aria-expanded={!collapsed}
                          className="flex w-full items-center gap-1.5 bg-[#1b1033]/[0.025] px-4 py-1.5 text-left text-[13px] font-semibold text-[#1b1033] dark:bg-white/[0.04] dark:text-[#f4f0ff]"
                          data-testid={`household-${item.key}`}
                        >
                          <ChevronDown
                            className={`h-3.5 w-3.5 text-[#4a3d66] transition-transform dark:text-gray-400 ${collapsed ? '-rotate-90' : ''}`}
                          />
                          {item.surname}
                          <span className="font-normal text-[#4a3d66] dark:text-gray-400">
                            · {item.count}
                          </span>
                        </button>
                      )
                    }
                    const guest = item.guest
                    return (
                    <div
                      key={guest.guest_id}
                      className="flex items-center justify-between gap-3 px-4 py-2.5 transition-colors hover:bg-[#1b1033]/[0.02] dark:hover:bg-white/5"
                      data-testid={`guest-row-${guest.guest_id}`}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        {/* RSVP status icon (replaces the letter avatar) — same
                            vocabulary as the Activity feed */}
                        {guest.rsvp_status === 'attending' ? (
                          <div
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-500/15"
                            title={language === 'ka' ? 'მოდის' : t.attending}
                          >
                            <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                          </div>
                        ) : guest.rsvp_status === 'declined' ? (
                          <div
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/15"
                            title={language === 'ka' ? 'ვერ მოდის' : t.declined}
                          >
                            <XCircle className="h-3.5 w-3.5 text-red-600" />
                          </div>
                        ) : guest.rsvp_status === 'maybe' ? (
                          <div
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/15"
                            title={t.maybe}
                          >
                            <HelpCircle className="h-3.5 w-3.5 text-amber-600" />
                          </div>
                        ) : (
                          <div
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#1b1033]/[0.06] dark:bg-white/10"
                            title={t.pending}
                          >
                            <Clock className="h-3.5 w-3.5 text-[#4a3d66] dark:text-gray-400" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="flex min-w-0 items-center gap-1.5 text-[15px] font-medium text-[#1b1033] dark:text-[#f4f0ff]">
                            <span className="truncate">{guest.name}</span>
                            {/* Plus-ones count (legacy plus_one boolean reads as +1) */}
                            {(guest.plus_ones > 0 || guest.plus_one) && (
                              <span className="shrink-0 rounded-md bg-[#1b1033]/[0.06] px-1.5 py-px text-xs font-medium tabular-nums text-[#4a3d66] dark:bg-white/10 dark:text-gray-300">
                                +{guest.plus_ones > 0 ? guest.plus_ones : 1}
                              </span>
                            )}
                            {guest.rsvp_message && (
                              <span className="truncate text-[13px] font-normal italic text-[#4a3d66] dark:text-gray-300">
                                “{guest.rsvp_message}”
                              </span>
                            )}
                          </p>
                          {(guest.email || guest.phone) && (
                            <p className="truncate text-[13px] text-[#4a3d66] dark:text-gray-400">
                              {guest.email || guest.phone}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5 sm:gap-2">
                        {/* Personal invite code — the credential this guest
                            needs when the event is invite-only, so it is only
                            surfaced when that mode is actually on. */}
                        {event.rsvp_invite_only && guest.invite_code && (
                          <button
                            type="button"
                            onClick={() => copyInviteCode(guest.invite_code)}
                            title={t.inviteCode}
                            aria-label={`${t.inviteCode} — ${guest.name}`}
                            className="inline-flex h-7 items-center gap-1 rounded-lg bg-[#8b5cf6]/10 px-2 font-mono text-[12px] font-semibold tracking-[0.12em] text-[#6d28d9] transition-colors hover:bg-[#8b5cf6]/20 dark:text-[#c4b5fd]"
                            data-testid={`guest-invite-code-${guest.guest_id}`}
                          >
                            {guest.invite_code}
                            <Copy className="h-3 w-3" />
                          </button>
                        )}

                        {/* Invite Status */}
                        {guest.invite_status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMarkInviteSent(guest.guest_id)}
                            className="h-7 rounded-lg px-2 text-[13px] text-[#ff4d8d]"
                          >
                            <Send className="h-3 w-3 sm:mr-1" />
                            <span className="hidden sm:inline">
                              {t.markSent}
                            </span>
                          </Button>
                        )}

                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditGuest(guest)}
                          aria-label={`${language === 'ka' ? 'რედაქტირება' : 'Edit'} ${guest.name}`}
                          className="h-7 w-7 rounded-lg"
                        >
                          <Edit className="h-3.5 w-3.5 text-[#4a3d66] dark:text-gray-400" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteGuest(guest.guest_id)}
                          aria-label={`${language === 'ka' ? 'წაშლა' : 'Delete'} ${guest.name}`}
                          className="h-7 w-7 rounded-lg"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    )
                  })}
                </div>
              )}
              {guestDisplayItems.length > 5 && (
                <button
                  type="button"
                  onClick={() => setGuestsExpanded((v) => !v)}
                  className="flex w-full items-center justify-center gap-1.5 border-t border-[#1b1033]/[0.06] py-2 text-[13px] font-medium text-[#ff4d8d] transition-colors hover:bg-[#1b1033]/[0.02] dark:border-white/10 dark:hover:bg-white/5"
                  data-testid="guests-show-more"
                >
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${guestsExpanded ? 'rotate-180' : ''}`}
                  />
                  {guestsExpanded
                    ? language === 'ka'
                      ? 'ნაკლების ჩვენება'
                      : 'Show less'
                    : language === 'ka'
                      ? `მეტის ნახვა (${guestDisplayItems.length - 5})`
                      : `Show more (${guestDisplayItems.length - 5})`}
                </button>
              )}
            </div>
          </div>

          {/* Activity feed — its own card, like the guest list */}
          <div className="mb-4 min-w-0">
            <div className="overflow-hidden rounded-2xl border border-[#1b1033]/[0.07] bg-[var(--surface)] shadow-[0_1px_3px_rgba(27,16,51,0.06)] dark:border-white/10 dark:shadow-none">
              <div className="border-b border-[#1b1033]/[0.06] px-4 py-2.5 dark:border-white/10">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold tracking-tight text-[#1b1033] dark:text-[#f4f0ff] font-display">
                    {language === 'ka' ? 'აქტივობა' : 'Activity'}
                  </h3>
                  {activity.length > 0 && (
                    <span className="rounded-full bg-[#1b1033]/[0.05] px-1.5 py-0.5 text-xs font-medium tabular-nums text-[#4a3d66] dark:bg-white/10 dark:text-gray-400">
                      {filteredActivity.length}
                      {filteredActivity.length !== activity.length
                        ? ` / ${activity.length}`
                        : ''}
                    </span>
                  )}
                </div>
                {activity.length > 0 && (
                  <div className="mt-2 flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center">
                    <div className="relative min-w-[140px] flex-1">
                      <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#4a3d66] dark:text-gray-400" />
                      <Input
                        value={activitySearch}
                        onChange={(e) => setActivitySearch(e.target.value)}
                        placeholder={language === 'ka' ? 'სახელი' : 'Name'}
                        className="h-8 rounded-lg border-[#1b1033]/[0.09] bg-transparent pl-8 text-[13px] dark:border-white/10 dark:bg-transparent"
                        data-testid="activity-search"
                      />
                    </div>
                    <Select value={activityType} onValueChange={setActivityType}>
                      <SelectTrigger
                        className="h-8 w-full rounded-lg border-[#1b1033]/[0.09] bg-transparent text-[13px] text-[#4a3d66] dark:border-white/10 dark:bg-transparent dark:text-gray-300 sm:w-[134px]"
                        data-testid="activity-type-filter"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          {language === 'ka' ? 'ყველა ტიპი' : 'All types'}
                        </SelectItem>
                        <SelectItem value="rsvp">RSVP</SelectItem>
                        <SelectItem value="gift">
                          {language === 'ka' ? 'საჩუქარი' : 'Gifts'}
                        </SelectItem>
                        <SelectItem value="message">
                          {language === 'ka' ? 'შეტყობინებები' : 'Messages'}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-1.5">
                      <Input
                        type="date"
                        value={activityFrom}
                        onChange={(e) => setActivityFrom(e.target.value)}
                        className="h-8 w-full rounded-lg border-[#1b1033]/[0.09] bg-transparent text-[13px] dark:border-white/10 dark:bg-transparent sm:w-[130px]"
                        aria-label={language === 'ka' ? 'დაწყების თარიღი' : 'From date'}
                        data-testid="activity-from"
                      />
                      <span className="text-xs text-[#4a3d66] dark:text-gray-400">–</span>
                      <Input
                        type="date"
                        value={activityTo}
                        onChange={(e) => setActivityTo(e.target.value)}
                        className="h-8 w-full rounded-lg border-[#1b1033]/[0.09] bg-transparent text-[13px] dark:border-white/10 dark:bg-transparent sm:w-[130px]"
                        aria-label={language === 'ka' ? 'დასრულების თარიღი' : 'To date'}
                        data-testid="activity-to"
                      />
                    </div>
                    {activityFiltersActive && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setActivityType('all')
                          setActivitySearch('')
                          setActivityFrom('')
                          setActivityTo('')
                        }}
                        className="h-8 rounded-lg px-2.5 text-[13px] text-[#4a3d66] dark:text-gray-400"
                        data-testid="activity-clear-filters"
                      >
                        {language === 'ka' ? 'გასუფთავება' : 'Clear'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {activity.length === 0 ? (
                <div className="p-8 text-center">
                  <Activity className="mx-auto mb-2 h-8 w-8 text-[#1b1033]/15 dark:text-white/15" />
                  <p className="text-sm text-[#4a3d66] dark:text-gray-400">
                    {language === 'ka'
                      ? 'ჯერ აქტივობა არ არის'
                      : 'No activity yet'}
                  </p>
                </div>
              ) : filteredActivity.length === 0 ? (
                <div className="p-8 text-center">
                  <Search className="mx-auto mb-2 h-8 w-8 text-[#1b1033]/15 dark:text-white/15" />
                  <p className="text-sm text-[#4a3d66] dark:text-gray-400">
                    {language === 'ka'
                      ? 'ფილტრს არცერთი აქტივობა არ შეესაბამება'
                      : 'No activity matches your filters'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-[#1b1033]/[0.06] dark:divide-white/[0.08]">
                  {(activityExpanded
                    ? filteredActivity
                    : filteredActivity.slice(0, 5)
                  ).map((entry, i) => {
                    const when = entry.created_at
                      ? new Date(entry.created_at).toLocaleString(
                          language === 'ka' ? 'ka-GE' : 'en-US',
                          {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          },
                        )
                      : ''
                    const statusLabel =
                      entry.status === 'attending'
                        ? language === 'ka'
                          ? 'მოდის'
                          : t.attending
                        : entry.status === 'declined'
                          ? language === 'ka'
                            ? 'ვერ მოდის'
                            : t.declined
                          : entry.status === 'maybe'
                            ? t.maybe
                            : t.pending
                    return (
                      <div key={i} className="flex items-center gap-2.5 px-4 py-2.5">
                        {entry.type === 'gift' ? (
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#ff4d8d]/10">
                            <Gift className="h-3.5 w-3.5 text-[#ff4d8d]" />
                          </div>
                        ) : entry.status === 'attending' ? (
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-500/15">
                            <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                          </div>
                        ) : entry.status === 'declined' ? (
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/15">
                            <XCircle className="h-3.5 w-3.5 text-red-600" />
                          </div>
                        ) : entry.status === 'maybe' ? (
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/15">
                            <HelpCircle className="h-3.5 w-3.5 text-amber-600" />
                          </div>
                        ) : (
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#1b1033]/[0.06] dark:bg-white/10">
                            <Clock className="h-3.5 w-3.5 text-[#4a3d66] dark:text-gray-400" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="flex min-w-0 items-center gap-1.5 text-[15px] text-[#1b1033] dark:text-[#f4f0ff]">
                            {entry.type === 'gift' ? (
                              <span className="truncate">
                                <span className="font-semibold">{entry.name}</span>{' '}
                                {language === 'ka' ? 'გაიღო' : 'gifted'}{' '}
                                <span className="font-semibold">
                                  {currencySymbol(entry.currency)}
                                  {formatMoney(entry.amount)}
                                </span>
                                {entry.item_title && (
                                  <span className="text-[#4a3d66] dark:text-gray-400">
                                    {' '}
                                    · {entry.item_title}
                                  </span>
                                )}
                              </span>
                            ) : (
                              <span className="truncate">
                                <span className="font-semibold">{entry.name}</span>
                                {entry.plus_ones > 0
                                  ? ` (+${entry.plus_ones})`
                                  : entry.plus_one
                                    ? ' (+1)'
                                    : ''}{' '}
                                <span className="text-[#4a3d66] dark:text-gray-400">
                                  — {statusLabel}
                                </span>
                              </span>
                            )}
                            {entry.message && (
                              <span className="truncate text-[13px] font-normal italic text-[#4a3d66] dark:text-gray-300">
                                “{entry.message}”
                              </span>
                            )}
                          </p>
                        </div>
                        <span className="shrink-0 whitespace-nowrap text-xs text-[#4a3d66] dark:text-gray-400">
                          {when}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
              {filteredActivity.length > 5 && (
                <button
                  type="button"
                  onClick={() => setActivityExpanded((v) => !v)}
                  className="flex w-full items-center justify-center gap-1.5 border-t border-[#1b1033]/[0.06] py-2 text-[13px] font-medium text-[#ff4d8d] transition-colors hover:bg-[#1b1033]/[0.02] dark:border-white/10 dark:hover:bg-white/5"
                  data-testid="activity-show-more"
                >
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${activityExpanded ? 'rotate-180' : ''}`}
                  />
                  {activityExpanded
                    ? language === 'ka'
                      ? 'ნაკლების ჩვენება'
                      : 'Show less'
                    : language === 'ka'
                      ? `მეტის ნახვა (${filteredActivity.length - 5})`
                      : `Show more (${filteredActivity.length - 5})`}
                </button>
              )}
            </div>
          </div>

          {/* Guest media vault — direct-to-storage guest uploads + host album */}
          <div className="glass-card rounded-xl p-3 mb-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-9 h-9 shrink-0 rounded-xl bg-[#fdeee9] dark:bg-[#281b47] flex items-center justify-center">
                  <Camera className="w-4 h-4 text-[#ff4d8d]" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold tracking-tight text-[#1b1033] dark:text-[#f4f0ff] font-display">
                    {language === 'ka' ? 'სტუმრების ალბომი' : 'Guest album'}
                  </h3>
                  <p className="truncate text-xs text-[#4a3d66] dark:text-gray-400">
                    {language === 'ka'
                      ? 'სტუმრები ტვირთავენ ფოტო/ვიდეოს ბმულით — თქვენ ათვალიერებთ და ჩამოტვირთავთ'
                      : 'Guests upload photos & videos via a link — you browse and download them here'}
                  </p>
                </div>
              </div>
              <Link
                to={`/events/${eventId}/media`}
                data-testid="open-media-album"
                className="shrink-0"
              >
                <Button variant="outline" className="rounded-full h-9 px-4 text-sm">
                  <ExternalLink className="w-4 h-4 mr-1.5" />
                  {language === 'ka' ? 'ალბომის გახსნა' : 'Open album'}
                </Button>
              </Link>
            </div>
          </div>
          </div>

          {/* Live invitation preview — exactly what guests see at the invite
              link. On xl it's a sticky sidebar; below xl it stacks under the
              management cards. */}
          <aside>
            <div className="space-y-3 xl:sticky xl:top-24">
              <div className="flex items-center justify-between gap-2 px-1">
                <div className="min-w-0">
                  <h3 className="font-display text-sm font-semibold text-[#1b1033] dark:text-[#f4f0ff]">
                    {language === 'ka' ? 'მოწვევის გადახედვა' : 'Invitation preview'}
                  </h3>
                  <p className="truncate text-xs text-[#4a3d66] dark:text-gray-400">
                    {language === 'ka' ? 'ასე ხედავენ სტუმრები' : 'What your guests see'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {/* Phone / desktop viewport toggle */}
                  <div
                    className="mr-1 flex items-center rounded-lg border border-[#1b1033]/10 p-0.5 dark:border-white/10"
                    role="group"
                    aria-label={language === 'ka' ? 'გადახედვის ზომა' : 'Preview size'}
                  >
                    <button
                      type="button"
                      onClick={() => setPreviewDevice('phone')}
                      aria-pressed={previewDevice === 'phone'}
                      aria-label={language === 'ka' ? 'მობილურის ხედი' : 'Phone preview'}
                      title={language === 'ka' ? 'მობილური' : 'Phone'}
                      className={`flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
                        previewDevice === 'phone'
                          ? 'bg-[#ff4d8d] text-white shadow-sm'
                          : 'text-[#4a3d66] hover:text-[#ff4d8d] dark:text-gray-400'
                      }`}
                      data-testid="preview-device-phone"
                    >
                      <Smartphone className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreviewDevice('desktop')}
                      aria-pressed={previewDevice === 'desktop'}
                      aria-label={language === 'ka' ? 'დესკტოპის ხედი' : 'Desktop preview'}
                      title={language === 'ka' ? 'დესკტოპი' : 'Desktop'}
                      className={`flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
                        previewDevice === 'desktop'
                          ? 'bg-[#ff4d8d] text-white shadow-sm'
                          : 'text-[#4a3d66] hover:text-[#ff4d8d] dark:text-gray-400'
                      }`}
                      data-testid="preview-device-desktop"
                    >
                      <Monitor className="h-4 w-4" />
                    </button>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setPreviewKey((k) => k + 1)}
                    title={language === 'ka' ? 'განახლება' : 'Refresh preview'}
                    aria-label={language === 'ka' ? 'განახლება' : 'Refresh preview'}
                    className="h-8 w-8 rounded-lg text-[#4a3d66] hover:text-[#ff4d8d] dark:text-gray-400"
                    data-testid="refresh-preview-btn"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <a
                    href={getInviteUrl()}
                    target="_blank"
                    rel="noreferrer"
                    title={language === 'ka' ? 'გახსნა ახალ ფანჯარაში' : 'Open invitation'}
                    aria-label={
                      language === 'ka'
                        ? 'მოწვევის გახსნა ახალ ფანჯარაში'
                        : 'Open invitation in a new tab'
                    }
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#4a3d66] transition-colors hover:text-[#ff4d8d] dark:text-gray-400"
                    data-testid="open-invite-btn"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>

              {!event.invite_link ? (
                <div className="mx-auto flex h-[480px] w-full max-w-[360px] items-center justify-center rounded-2xl border border-[#1b1033]/10 px-6 text-center text-sm text-[#4a3d66] dark:border-white/10 dark:text-gray-400">
                  {language === 'ka'
                    ? 'მოწვევის ბმული ჯერ არ არის ხელმისაწვდომი'
                    : 'Invitation link is not available yet'}
                </div>
              ) : previewDevice === 'phone' ? (
                /* Phone mockup wrapping a live iframe of the public invite page.
                   Height budget = viewport − sticky offset (6rem) − preview header
                   − phone bezel, so the whole phone fits on screen without scroll. */
                <div className="mx-auto w-full max-w-[360px] overflow-hidden rounded-[2.25rem] border-[10px] border-[#1b1033] bg-[#1b1033] shadow-2xl shadow-[#1b1033]/25 dark:border-black dark:bg-black">
                  <div className="overflow-hidden rounded-[1.6rem] bg-white dark:bg-[#1e1438]">
                    {/* Same height budget as the desktop preview frame
                        (viewport − header − preview chrome − share buttons −
                        margins). Below xl the page itself scrolls, so a fixed
                        height keeps the phone frame usable instead of letting
                        the viewport math collapse it. */}
                    <iframe
                      key={`phone-${previewKey}`}
                      title="Invitation preview"
                      src={`/invite/${event.invite_link}?hostEdit=1`}
                      className="h-[520px] min-h-[360px] w-full border-0 bg-white dark:bg-[#1e1438] xl:h-[calc(100dvh-28rem)]"
                    />
                  </div>
                </div>
              ) : (
                /* Desktop browser chrome — renders the invite at 1280px, scaled to fit */
                <div className="overflow-hidden rounded-xl border border-[#1b1033]/12 shadow-2xl shadow-[#1b1033]/15 dark:border-white/10">
                  <div className="flex items-center gap-1.5 border-b border-[#1b1033]/10 bg-[#1b1033]/[0.04] px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.04]">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
                  </div>
                  {/* Height = viewport − app header − preview header/chrome −
                      share-buttons card − breathing room top and bottom
                      (≈23rem total). Below xl the page scrolls, so use a fixed
                      height instead of the viewport budget. */}
                  <div
                    ref={measurePreview}
                    className="relative h-[520px] min-h-[360px] w-full overflow-hidden bg-white dark:bg-[#1e1438] xl:h-[calc(100dvh-28rem)]"
                  >
                    {desktopScale > 0 && (
                      <iframe
                        key={`desktop-${previewKey}`}
                        title="Invitation preview"
                        src={`/invite/${event.invite_link}?hostEdit=1`}
                        style={{
                          width: `${DESKTOP_PREVIEW_WIDTH}px`,
                          height: `${desktopViewportH}px`,
                          transform: `scale(${desktopScale})`,
                          transformOrigin: 'top left',
                          border: 0,
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>

        {/* Quick-edit dialog — opened by clicking editable elements inside the
            invitation preview */}
        <Dialog
          open={!!quickEdit}
          onOpenChange={(open) => {
            if (!open) setQuickEdit(null)
          }}
        >
          <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="font-display">
                {quickEdit &&
                  (language === 'ka'
                    ? QUICK_EDIT_FIELDS[quickEdit.field].ka
                    : QUICK_EDIT_FIELDS[quickEdit.field].en)}
              </DialogTitle>
            </DialogHeader>
            {quickEdit && (
              <div className="space-y-4 py-2">
                {quickEdit.field === 'cover' ? (
                  <Button
                    onClick={() => {
                      setQuickEdit(null)
                      coverInputRef.current?.click()
                    }}
                    className="w-full h-11 btn-gradient text-white rounded-xl"
                    data-testid="quick-edit-cover-btn"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {language === 'ka'
                      ? 'ახალი სურათის ატვირთვა'
                      : 'Upload a new photo'}
                  </Button>
                ) : (
                  <>
                    {QUICK_EDIT_FIELDS[quickEdit.field].type === 'textarea' ? (
                      <textarea
                        value={quickEdit.value}
                        onChange={(e) =>
                          setQuickEdit({ ...quickEdit, value: e.target.value })
                        }
                        rows={4}
                        maxLength={2000}
                        className="w-full rounded-xl border border-[#1b1033]/12 dark:border-white/10 bg-white dark:bg-white/5 p-3 text-sm text-[#1b1033] dark:text-[#f4f0ff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff4d8d]/40"
                        data-testid="quick-edit-input"
                      />
                    ) : (
                      <Input
                        type={QUICK_EDIT_FIELDS[quickEdit.field].type}
                        value={quickEdit.value}
                        onChange={(e) =>
                          setQuickEdit({ ...quickEdit, value: e.target.value })
                        }
                        maxLength={
                          quickEdit.field === 'location'
                            ? 300
                            : quickEdit.field === 'dress_code'
                              ? 160
                              : 120
                        }
                        className="h-11 rounded-xl"
                        data-testid="quick-edit-input"
                      />
                    )}
                    <Button
                      onClick={saveQuickEdit}
                      disabled={
                        savingQuickEdit ||
                        (quickEdit.field === 'title' && !quickEdit.value.trim())
                      }
                      className="w-full h-11 btn-gradient text-white rounded-xl"
                      data-testid="quick-edit-save-btn"
                    >
                      {savingQuickEdit ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : language === 'ka' ? (
                        'შენახვა'
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
      <AppFooter maxWidth="max-w-none" />
    </div>
  )
}

export default EventDetail
